import React from 'react';
import { useIntl } from 'react-intl';
import settings from 'configurations/application';
import Input from 'components/input';
import type { InputElement } from 'components/input';
import Shimmer from 'components/shimmer';
import Row, { type RowOnChange } from 'components/form/row';
import is from 'utils/is';
import deepEqual from 'utils/deep-equal';

import { testId } from '../../registration.settings';
import type { SignupFormField } from '../../contracts';
import type { EmailAvailability } from '../../../registration.service';

import messages from './email.i18n';

interface EmailInput extends SignupFormField<string | undefined>, Pick<InputElement, 'variant' | 'spaceless'> {
  labelType?: 'default' | 'change-address';
  skeleton?: boolean;
  availability: EmailAvailability;
  loading: boolean;
  onChange: RowOnChange<string>;
}

const Email: React.FunctionComponent<EmailInput> = (props) => {
  const {
    labelType = 'default',
    initialValue,
    rejectionReason,
    loading,
    skeleton,
    availability,
    error,
    disabled,
    variant = 'standard',
    spaceless,
    onChange,
  } = props;
  const { formatMessage } = useIntl();
  const [invalid, setInvalid] = React.useState<boolean>(Boolean(initialValue) && !is.email(initialValue ?? ''));
  const [empty, toggleEmptyState] = React.useState<boolean>(false);

  React.useEffect(() => {
    setInvalid(Boolean(initialValue) && !is.email(initialValue ?? ''));
  }, [initialValue]);

  const isValueValid = React.useCallback(
    (value: string): boolean => {
      return (!rejectionReason ? true : initialValue !== value) && is.email(value);
    },
    [rejectionReason, initialValue]
  );

  const handleOnChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (!is.func(onChange)) {
        return;
      }

      const { value } = event.target;
      const valid = isValueValid(value);

      onChange(value, valid);

      if (value.trim() !== '') {
        toggleEmptyState(false);
        setInvalid(false);

        return;
      }

      if (valid) {
        setInvalid(!valid);
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availability, isValueValid, onChange]
  );

  const handleOnBlur = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (!is.func(onChange)) {
        return;
      }

      const { value } = event.target;
      const valid = isValueValid(value);

      onChange(value, valid);

      toggleEmptyState(value.trim() === '');
      setInvalid(!valid);
    },
    [isValueValid, onChange]
  );

  const hasIssue = (!loading && availability.status !== 'available') || error || invalid || empty;

  const formRowHint = React.useMemo(() => {
    if (!hasIssue) {
      return;
    }

    if (!loading && availability.status === 'invalid') {
      return formatMessage(messages.pages.signup.registration.components.email.invalid);
    }

    if (!loading && availability.status === 'unavailable') {
      return formatMessage(messages.pages.signup.registration.components.email.taken);
    }

    if (empty) {
      return formatMessage(messages.pages.signup.registration.components.email.empty);
    }

    if (invalid) {
      return formatMessage(messages.pages.signup.registration.components.email.invalid);
    }
  }, [availability.status, empty, formatMessage, hasIssue, invalid, loading]);

  if (variant === 'outline') {
    return (
      <Shimmer loading={skeleton} className="d-block rounded-2">
        <Input
          testId={testId.email}
          type={is.mobileDevice ? 'email' : 'text'}
          name="email"
          value={initialValue}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          loading={loading}
          error={Boolean(rejectionReason) || hasIssue}
          maxLength={+settings.fields.email.validation.MAX}
          disabled={disabled}
          variant={variant}
          spaceless={spaceless}
          label={
            labelType === 'default'
              ? formatMessage(messages.pages.signup.registration.components.email.labelShort)
              : formatMessage(messages.pages.signup.registration.components.email.changeAddressShort)
          }
          helperText={rejectionReason || formRowHint}
        />
      </Shimmer>
    );
  }

  return (
    <Row
      label={
        labelType === 'default'
          ? formatMessage(messages.pages.signup.registration.components.email.label)
          : formatMessage(messages.pages.signup.registration.components.email.changeAddress)
      }
      hint={formRowHint}
      warning={rejectionReason || hasIssue}
    >
      <Shimmer loading={skeleton} className="d-block">
        <Input
          testId={testId.email}
          type={is.mobileDevice ? 'email' : 'text'}
          name="email"
          value={initialValue}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          loading={loading}
          error={hasIssue}
          maxLength={+settings.fields.email.validation.MAX}
          disabled={disabled}
        />
      </Shimmer>
    </Row>
  );
};

export type { EmailInput };
export default React.memo(Email, deepEqual);
